import { modeUIContext, setIsLoading, showErrorNotification } from '@moderepo/mode-ui-state';
import { ApiError } from '@moderepo/mode-apis';
import React, { useCallback, useContext } from 'react';
import { initialRitzDataState, ritzDataContext, RitzDataState, RitzDataStateAction, ritzDataStateReducer }
    from '@moderepo/mode-data-state-ritz';
import { getErrorMessage, getLocalStorageItem } from '@moderepo/mode-app-components';
import { AgentLoginInfo } from '@moderepo/mode-internal-apis';
import { GenericGlobalDataStateProvider, initialUserAppDataState, userAppDataContext, UserAppDataState, UserAppDataStateAction,
    userAppDataStateReducer } from '@moderepo/mode-data-state-user-app';
import { initialRCDataState, rcDataContext, RCDataState, RCDataStateAction, rcDataStateReducer } from '@moderepo/mode-data-state-robot-cloud';
import { LS_KEY_LOGIN_INFO } from '../../utils/constants';

// get login cached login info from local storage
const cachedLoginInfo: AgentLoginInfo = getLocalStorageItem(LS_KEY_LOGIN_INFO, {});
const isLoggedIn = Boolean(cachedLoginInfo && cachedLoginInfo.authenticated && cachedLoginInfo.xsrfGuard && cachedLoginInfo.agentId);
const initialState: RitzDataState = {
    ...initialRitzDataState,
    auth: {
        ...initialRitzDataState.auth,
        agentLoginInfo: isLoggedIn ? cachedLoginInfo : undefined,
    },
};


/**
 * This is the wrapper for the GlobalDataStateProvider. The GlobalDataStateProvider will fire an event when it start loading or when there
 * is an error and this container will capture those events and show the loading screen or error message accordingly.
 * @param param
 */
export const GlobalDataStateProviderContainer = ({ children }: React.PropsWithChildren<React.ReactNode>) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);


    const onLoading = useCallback((value: boolean) => {
        uiDispatch(setIsLoading(value));
    }, [uiDispatch]);


    const onError = useCallback((error: ApiError) => {
        uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
    }, [uiDispatch]);


    return (
        <GenericGlobalDataStateProvider<RitzDataState, RitzDataStateAction>
            context={ritzDataContext}
            initialState={initialState}
            stateReducer={ritzDataStateReducer}
            onLoading={onLoading}
            onError={onError}
        >
            <GenericGlobalDataStateProvider<UserAppDataState, UserAppDataStateAction>
                context={userAppDataContext}
                initialState={initialUserAppDataState}
                stateReducer={userAppDataStateReducer}
                onLoading={onLoading}
                onError={onError}
            >
                <GenericGlobalDataStateProvider<RCDataState, RCDataStateAction>
                    context={rcDataContext}
                    initialState={initialRCDataState}
                    stateReducer={rcDataStateReducer}
                    onLoading={onLoading}
                    onError={onError}
                >
                    {children}
                </GenericGlobalDataStateProvider>
            </GenericGlobalDataStateProvider>
        </GenericGlobalDataStateProvider>
    );
};
